import React, { useEffect, useState } from "react";
import { Col, Row, Button } from "reactstrap";
import { SearchableSelectHookForm } from "../../layout/HookFormInputs";
import { Controller, useForm } from "react-hook-form";
import {
  getCategoryListDataForAssesment,
  getReviewQuestionsByCategoryId,
  updateQuestionSequence
} from "./Action";
import { useDispatch } from 'react-redux';
import { EMPTY_DATA } from "../../../config/constants";
import NoContentFound from "../../common/NoContentFound";
import Toaster from "../../common/Toaster";
import LoaderCustom from "../../common/LoaderCustom";
import HeaderTitle from "../../common/HeaderTitle";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function ReviewAssesmentQuestions() {
  const [loading, setLoading] = useState(false);
  const [categoryMasterData, setCategoryMasterData] = useState([]);
  const [reviewQuestoinSection, setReviewQuestionSection] = useState([]);
  const [categoryID, setCategoryID] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    getAllCategoryMasterData();
  }, []);

  const getReviewQuestionList = async (categoryId) => {
    setLoading(true);

    try {
      const response = await getReviewQuestionsByCategoryId(categoryId);
      if (response && response.Data) {
        const questionsData = response.Data;
        setReviewQuestionSection(questionsData);
      } else {
        setReviewQuestionSection([]);
      }
    } catch (error) {
      Toaster.error(error?.response?.data);
      setReviewQuestionSection([]);
    } finally {
      setLoading(false);
    }
  };

  const getAllCategoryMasterData = () => {
    setLoading(true);
    getCategoryListDataForAssesment()
      .then((res) => {
        setCategoryMasterData(res?.Data);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  const renderListing = (label) => {
    if (label === "categoryMasterData") {
      return categoryMasterData.map((categoryMasterData) => ({
        value: categoryMasterData.Id,
        label: categoryMasterData.SupplierCategory,
      }));
    }
  };

  const handleCategoryChange = (categoryId) => {
    setCategoryID(categoryId?.value);
  };

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const onSubmit = async (data) => {
    if (errors.CategoryId && !data.CategoryId) {
      return;
    }
    getReviewQuestionList(categoryID);
  };

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  const callUpdateQuestionSequence = async (updatedSequences) => {
    try {
      const res = await dispatch(updateQuestionSequence(updatedSequences));
      if (res.IsSuccessful) {
        Toaster.success(res?.Data[0]?.Message);
      } else {
        Toaster.error('Failed to update question sequence.');
      }
    } catch (error) {
      Toaster.error('An error occurred.');
    }
  };

  const handleDragEnd = (event, groupIndex) => {
    const { active, over } = event;
    if (!over) return;

    const activeIndex = parseInt(active.id);
    const overIndex = parseInt(over.id);

    setReviewQuestionSection((prev) => {
      const updatedSections = [...prev];
      const groupQuestions = updatedSections[groupIndex].Questions;
      const movedQuestion = groupQuestions[activeIndex];
      const remainingQuestions = groupQuestions.filter(
        (_, index) => index !== activeIndex
      );

      const newQuestions = [
        ...remainingQuestions.slice(0, overIndex),
        movedQuestion,
        ...remainingQuestions.slice(overIndex),
      ];

      newQuestions.forEach((question, index) => {
        question.sequence = index + 1;
      });

      updatedSections[groupIndex].Questions = newQuestions;

      const updatedSequences = newQuestions.map((q) => ({
        Id: q.Id,
        sequence: q.sequence,
        categoryID: categoryID,
      }));

      callUpdateQuestionSequence(updatedSequences);

      return updatedSections;
    });
  };

  const SortableItem = ({ id, question }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };
    const columnHeaderStyle = {
      minWidth: "120px", // Set a minimum width for the header column
      width: "30%", // Set a fixed width for the header column
      backgroundColor: "#e7f2fc", // Setting a background color for the header column
      padding: "10px" // Adding padding to header column
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="p-3 border rounded bg-white shadow-sm mb-2"
      >
        <table className="table table-bordered">
          <tbody>
            <tr>
              <th style={columnHeaderStyle}>Question</th>
              <td style={columnHeaderStyle}>{question.AssessmentQuestion}</td>
            </tr>
            {[0, 1, 2, 3, 4].map((optionIndex) => (
              <tr key={optionIndex}>
                <th>Option {optionIndex}</th>
                <td>{question[`Option_${optionIndex}_Label`]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderGroupContent = (checkpoint, groupIndex) => (
    <DndContext
      key={groupIndex}
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(event) => handleDragEnd(event, groupIndex)}
    >
      <SortableContext
        items={checkpoint.Questions.map((q, i) => ({ ...q, id: i.toString() }))}
        strategy={verticalListSortingStrategy}
      >
        <div className="mb-4 p-3 border rounded bg-light">
          <HeaderTitle title={<>{checkpoint.CheckPointGroupName}</>} />
          <Row>
            {checkpoint.Questions.map((question, questionIndex) => (
              <Col key={questionIndex} md="12">
                <SortableItem
                  id={questionIndex.toString()}
                  question={question}
                />
              </Col>
            ))}
          </Row>
        </div>
      </SortableContext>
    </DndContext>
  );

  if (loading) {
    return (
      <div>
        <LoaderCustom customClass="simulation-Loader" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-100">
      <Row>
        <Col md="4" className="d-flex align-items-center">
          <SearchableSelectHookForm
            label={"Category"}
            name={"CategoryId"}
            placeholder={"Select"}
            Controller={Controller}
            control={control}
            rules={{ required: true }}
            register={register}
            options={renderListing("categoryMasterData")}
            mandatory={true}
            handleChange={(value) => {
              handleCategoryChange(value);
            }}
            errors={errors?.CategoryId}
            disabled={false}
          />
          <div className="text-left ml-3 pt-2 mt-2">
            <Button type="submit" className="btn btn-primary">
              View
            </Button>
          </div>
        </Col>
        <Col md="12">
          <hr className="my-4" />
        </Col>
        <Col md="12">
          {reviewQuestoinSection.length === 0 ? (
            <NoContentFound
              title={EMPTY_DATA}
              customClassName="no-content-found"
            />
          ) : (
            reviewQuestoinSection.map((checkpoint, index) => (
              <div key={index} className="mb-3">
                {renderGroupContent(checkpoint, index)}
              </div>
            ))
          )}
        </Col>
      </Row>
    </form>
  );
}

export default ReviewAssesmentQuestions;